import { useMemo, useState } from "react";
import { InfoWindow, Map, Marker } from "@vis.gl/react-google-maps";
import { socLabel } from "../lib/telemetryReadings";
import { GOOGLE_MAPS_MAP_ID, hasGoogleMapsKey } from "../lib/googleMaps";
import { MapKeyMissing } from "./GoogleMapsProvider";
import { FitBounds, type LatLngBoundsTuple } from "./GoogleMapOverlays";

const MAP_HEIGHT = 360;

/** An 8px-radius circle centred on the anchor, as an SVG path. */
const CIRCLE_PATH = "M -8,0 a 8,8 0 1,0 16,0 a 8,8 0 1,0 -16,0";

export interface FleetMapUnit {
  device_id: string;
  latitude: number;
  longitude: number;
  has_gps_lock: boolean;
  status: string;
  statusLabel: string;
  color: string;
  battery_charge_percent: number | null;
}

/** A point is mappable only with a real GPS fix and finite, non-null
 *  coordinates. (0,0) is treated as "no fix" — it's in the ocean off Africa
 *  and is the classic uninitialized-GPS sentinel. */
function hasValidLocation(u: FleetMapUnit): boolean {
  return (
    u.has_gps_lock &&
    Number.isFinite(u.latitude) &&
    Number.isFinite(u.longitude) &&
    !(u.latitude === 0 && u.longitude === 0)
  );
}

function boundsOfUnits(units: FleetMapUnit[]): LatLngBoundsTuple | null {
  if (units.length === 0) return null;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const u of units) {
    minLat = Math.min(minLat, u.latitude);
    maxLat = Math.max(maxLat, u.latitude);
    minLng = Math.min(minLng, u.longitude);
    maxLng = Math.max(maxLng, u.longitude);
  }
  return [
    [minLat, minLng],
    [maxLat, maxLng],
  ];
}

function Placeholder({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        height: MAP_HEIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#dce8f0",
        color: "#6b7280",
        fontSize: 12,
        fontFamily: "Raleway, sans-serif",
      }}
    >
      {children}
    </div>
  );
}

export function FleetMap({
  units,
  onSelect,
}: {
  units: FleetMapUnit[];
  onSelect?: (deviceId: string) => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const located = useMemo(() => units.filter(hasValidLocation), [units]);
  const bounds = useMemo(() => boundsOfUnits(located), [located]);
  const fitKey = useMemo(
    () => located.map((u) => u.device_id).sort().join(","),
    [located],
  );
  const open = located.find((u) => u.device_id === openId) ?? null;

  if (!hasGoogleMapsKey) return <MapKeyMissing height={MAP_HEIGHT} />;

  if (located.length === 0) {
    return <Placeholder>No units are reporting a GPS location right now.</Placeholder>;
  }

  return (
    <Map
      mapId={GOOGLE_MAPS_MAP_ID}
      // Defaults only — FitBounds takes over as soon as the map is ready, and
      // re-fits whenever the set of located units changes.
      defaultCenter={{ lat: located[0].latitude, lng: located[0].longitude }}
      defaultZoom={13}
      style={{ height: MAP_HEIGHT, width: "100%" }}
      gestureHandling="cooperative"
      scrollwheel={false}
      mapTypeControl={false}
      streetViewControl={false}
      fullscreenControl={false}
      clickableIcons={false}
      onClick={() => setOpenId(null)}
    >
      {/* Re-frame when the set of mapped units changes, not on every GPS
          update — live positions move constantly and a map that re-centres
          every frame can't be read. */}
      <FitBounds bounds={bounds} padding={40} maxZoom={16} fitKey={fitKey} />

      {located.map((u) => (
        <Marker
          key={u.device_id}
          position={{ lat: u.latitude, lng: u.longitude }}
          title={`${u.device_id} — ${u.statusLabel}`}
          onClick={() => setOpenId(u.device_id)}
          // A symbol icon rather than an Advanced Marker: it reproduces the
          // status-coloured dot exactly and needs no Cloud map ID, so the map
          // works with nothing but an API key.
          icon={{
            // An SVG circle path rather than google.maps.SymbolPath.CIRCLE:
            // the enum only exists once the API script has loaded, and these
            // props are evaluated during React's render, which can run first.
            path: CIRCLE_PATH,
            scale: 1,
            fillColor: u.color,
            fillOpacity: 1,
            strokeColor: "#fff",
            strokeWeight: 2,
          }}
        />
      ))}

      {open && (
        <InfoWindow
          position={{ lat: open.latitude, lng: open.longitude }}
          pixelOffset={[0, -10]}
          onCloseClick={() => setOpenId(null)}
        >
          <div style={{ fontFamily: "Raleway, sans-serif", fontSize: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>{open.device_id}</div>
            <div>{open.statusLabel}</div>
            <div>Battery: {socLabel(open.battery_charge_percent)}</div>
            {onSelect && (
              <button
                onClick={() => onSelect(open.device_id)}
                style={{
                  marginTop: 6,
                  border: 0,
                  background: "#5E85C4",
                  color: "#fff",
                  borderRadius: 6,
                  padding: "4px 10px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                View sessions →
              </button>
            )}
          </div>
        </InfoWindow>
      )}
    </Map>
  );
}
